import { type AxiosResponse } from 'axios'
import type { AxiosOptions } from 'shared/api'
import { baseApi } from 'entities/Auth'
import { BaseAnalyticsApi } from './baseAnalyticsApi'
import type {
  AnalyticsResponse,
  IAnalyticsExportParams,
  IAnalyticsExportResponse,
  IAnalyticsParams,
  ICreditUsageChartResponse,
  ICreditUsageStatisticResponse,
  IParamsCreditUsageAnalyticsList,
  IParamsCreditUsageList,
  IParamsCreditUsageSourcesExport,
  IParamsCreditUsageSourcesList,
  IParamsCreditUsageSourcesSummary,
  IResponseCreditUsageAnalyticsDetails,
  IResponseCreditUsageAnalyticsList,
  IResponseCreditUsageSourcesList,
  IResponseCreditUsageSourcesSummary,
} from './types'

class CreditUsageApi extends BaseAnalyticsApi<
  ICreditUsageStatisticResponse,
  ICreditUsageChartResponse
> {
  constructor() {
    super('credits-usage')
  }

  exportAnalytics = (
    params: IAnalyticsExportParams,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IAnalyticsExportResponse>> => {
    return baseApi.get('analytics/credits/usage-totals-export', {
      params,
      ...options,
    })
  }

  preloadCreditUsageAnalytics = (
    params: IAnalyticsParams,
    options?: AxiosOptions
  ): Promise<
    AxiosResponse<AnalyticsResponse<ICreditUsageStatisticResponse, ICreditUsageChartResponse>>
  > => {
    return baseApi.get('analytics/credits/usage-totals', {
      params: {
        ...params,
        preload_type: 'previous',
      },
      ...options,
    })
  }

  getCreditUsageAnalytics = (
    params: IAnalyticsParams,
    options?: AxiosOptions
  ): Promise<
    AxiosResponse<AnalyticsResponse<ICreditUsageStatisticResponse, ICreditUsageChartResponse>>
  > => {
    return baseApi.get('analytics/credits/usage-totals', {
      params,
      ...options,
    })
  }

  getCreditUsageAnalyticsList = (
    params: IParamsCreditUsageAnalyticsList,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseCreditUsageAnalyticsList>> => {
    return baseApi.get('analytics/credits/usage-list', {
      params,
      ...options,
    })
  }

  getCreditUsageAnalyticsDetails = (
    params: IParamsCreditUsageList[]
  ): Promise<AxiosResponse<IResponseCreditUsageAnalyticsDetails>> => {
    return baseApi.post('core/analytics/credits-usage/details', params)
  }

  getCreditUsageSourcesList = (
    params: IParamsCreditUsageSourcesList,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseCreditUsageSourcesList>> => {
    return baseApi.get('analytics/credits/usage-sources-list', {
      params,
      ...options,
    })
  }

  exportSources = (
    params: IParamsCreditUsageSourcesExport,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IAnalyticsExportResponse>> => {
    return baseApi.get('analytics/credits/usage-sources-export', {
      params,
      ...options,
    })
  }

  getCreditUsageSourcesSummary = (
    params: IParamsCreditUsageSourcesSummary,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseCreditUsageSourcesSummary>> => {
    return baseApi.get('analytics/credits/usage-sources-totals', {
      params,
      ...options,
    })
  }
}

export const CreditUsageAnalyticsApi = new CreditUsageApi()
